import { api } from '@/api';
import {
  AssignedUsersByGroup,
  Case,
  CaseResourceCombined,
  CaseResourceType,
  CaseUserGroup,
  RandomizationFilterType,
  RandomizationResultType,
} from '@/api/apis/case';
import { PaginatedBody, Uuid } from '@/api/types';
import { queryClient } from '@/queries';
import { caseQueries } from '@/queries/case/case.query';
import { caseQueryKeys } from '@/queries/case/case.keys';
import { CaseBoardListFilters } from '@/store/cases-board.store';
import {
  addCaseToList,
  removeCaseFromAllListCaches,
  removeCaseFromInfiniteList,
  updateCaseInInfiniteList,
} from './case-cache.utils';

export const formatFiltersData = (filters: CaseBoardListFilters, searchQuery: string, myEmployeeId?: Uuid) => {
  const organType = filters.organ.join(',') || undefined;
  const donorType = filters.donorType[0];
  const logisticProviderTypes = filters.logisticProviderTypes.join(',') || undefined;
  const groundConfirmationStatuses = filters.groundConfirmationStatuses.join(',') || undefined;
  const quoteStatuses = filters.quoteStatuses.join(',') || undefined;
  const routePlanningStatuses = filters.routePlanningStatuses.join(',') || undefined;
  const isFavouriteFilter = filters.favorites || undefined;
  const isModifiedFilter = filters.recent || undefined;
  const assignedToMe = filters.assignedMe;
  let assignedUsers = [...filters.assignedUsers];
  if (assignedToMe && myEmployeeId) {
    assignedUsers = [myEmployeeId];
  }
  const {
    trialProgramIds,
    serviceRegionIds,
    txCenterIds,
    opoIds,
    donorHospitalIds,
    opoRequest,
    isReallocatedFromSource,
  } = filters;
  const randomizationResultFilter = filters.randomizationResultTypes;

  return {
    searchQuery: searchQuery || undefined,
    assignedUsers,
    organType,
    donorType,
    logisticProviderTypes,
    groundConfirmationStatuses,
    quoteStatuses,
    routePlanningStatuses,
    isFavouriteFilter,
    isModifiedFilter,
    trialProgramIds,
    randomizationResultFilter,
    serviceRegionIds,
    txCenterIds,
    opoIds,
    donorHospitalIds,
    opoRequest: opoRequest || undefined,
    isReallocatedFromSource: isReallocatedFromSource || undefined,
  };
};

export type BoardListQueryParams = ReturnType<typeof formatFiltersData> & { caseStatuses?: string; caseIds?: string };

const matchesCsv = (value: string | undefined, csv?: string): boolean => {
  if (!csv) return true;
  return value !== undefined && csv.split(',').includes(value);
};

const matchesAnyOf = <T>(value: T | undefined, values?: T[]): boolean => {
  if (!values?.length) return true;
  return value !== undefined && values.includes(value);
};

const ASSIGNED_USERS_FILTER_RESOURCE_TYPES = new Set<CaseResourceType>([
  CaseResourceType.Surgeon,
  CaseResourceType.ClinicalSpecialist,
]);

const matchesAssignedUsers = (
  assignedEmployees: AssignedUsersByGroup,
  resources: CaseResourceCombined[],
  employeeIds?: string[],
): boolean => {
  if (!employeeIds?.length) return true;

  const isAssignedEmployee = Object.entries(assignedEmployees).some(
    ([group, users]) =>
      group !== CaseUserGroup.CaseCreator &&
      !!users?.some((user) => !!user.employeeId && employeeIds.includes(user.employeeId)),
  );

  const isAssignedResource = resources.some(
    ({ resourceType, resourceId }) =>
      ASSIGNED_USERS_FILTER_RESOURCE_TYPES.has(resourceType) && employeeIds.includes(resourceId),
  );

  return isAssignedEmployee || isAssignedResource;
};

const toRandomizationFilterType = (result?: RandomizationResultType): RandomizationFilterType => {
  if (result === RandomizationResultType.OCS) return RandomizationFilterType.OCS;
  if (result === RandomizationResultType.SCS) return RandomizationFilterType.SCS;
  return RandomizationFilterType.NotRandomized;
};

export const caseMatchesListParams = (caseInfo: Case, params: Partial<BoardListQueryParams>): boolean =>
  matchesCsv(caseInfo.status, params.caseStatuses) &&
  matchesCsv(caseInfo.organType, params.organType) &&
  (!params.donorType || params.donorType === caseInfo.donorType) &&
  matchesCsv(caseInfo.logisticProviderTypes, params.logisticProviderTypes) &&
  matchesCsv(caseInfo.routePlanningStatus, params.routePlanningStatuses) &&
  matchesCsv(caseInfo.quote?.status, params.quoteStatuses) &&
  (!params.isFavouriteFilter || caseInfo.isFavourite) &&
  (!params.opoRequest || caseInfo.opoRequest) &&
  matchesAssignedUsers(caseInfo.assignedEmployees, caseInfo.resources, params.assignedUsers) &&
  matchesAnyOf(caseInfo.trialProgram?.id, params.trialProgramIds) &&
  matchesAnyOf(caseInfo.serviceRegionId, params.serviceRegionIds) &&
  matchesAnyOf(caseInfo.txCenterId, params.txCenterIds) &&
  matchesAnyOf(caseInfo.opoId ?? undefined, params.opoIds) &&
  matchesAnyOf(caseInfo.donorHospitalId, params.donorHospitalIds) &&
  matchesCsv(caseInfo.caseId, params.caseIds) &&
  (!params.randomizationResultFilter?.length ||
    params.randomizationResultFilter.includes(toRandomizationFilterType(caseInfo.randomization?.result)));

export const reconcileCaseInInfiniteListCache =
  (
    caseInfo: Case,
    params: BoardListQueryParams,
  ): ((prevData?: { pages: PaginatedBody<Case>[]; pageParams: unknown[] }) =>
    | {
        pages: PaginatedBody<Case>[];
        pageParams: unknown[];
      }
    | undefined) =>
  (prevData) => {
    if (!prevData) return prevData;

    const isCached = prevData.pages.some((page) => page.items.some((c) => c.caseId === caseInfo.caseId));
    const stillMatches = caseMatchesListParams(caseInfo, params);

    if (isCached && !stillMatches) return removeCaseFromInfiniteList(caseInfo.caseId)(prevData);
    if (isCached && stillMatches) {
      return updateCaseInInfiniteList(caseInfo)(prevData) as { pages: PaginatedBody<Case>[]; pageParams: unknown[] };
    }
    if (!isCached && stillMatches) {
      return {
        ...prevData,
        pages: prevData.pages.map((page, index) =>
          index === 0 ? (addCaseToList(caseInfo, 'start')(page) as PaginatedBody<Case>) : page,
        ),
      };
    }

    return prevData;
  };

const caseTouchesCache = (caseInfo: Case, params: BoardListQueryParams, items: Case[]): boolean =>
  items.some((c) => c.caseId === caseInfo.caseId) || caseMatchesListParams(caseInfo, params);

// searchQuery and isModifiedFilter can't be derived from a Case object
const hasUnreconcilableFilters = (params: BoardListQueryParams): boolean =>
  !!params.searchQuery || !!params.isModifiedFilter;

const reconcileQueriesByPrefix = <TData>(
  queryKey: readonly unknown[],
  caseInfo: Case,
  reconcile: (caseInfo: Case, params: BoardListQueryParams) => (prevData?: TData) => TData | undefined,
  getItems: (data: TData) => Case[],
): void => {
  queryClient.getQueriesData<TData>({ queryKey }).forEach(([key, data]) => {
    const params = key[2] as BoardListQueryParams | undefined;
    if (!params) return;

    if (hasUnreconcilableFilters(params)) {
      queryClient.invalidateQueries({ queryKey: key, exact: true });
      return;
    }

    queryClient.setQueryData(key, reconcile(caseInfo, params)(data));

    if (data && caseTouchesCache(caseInfo, params, getItems(data))) {
      queryClient.invalidateQueries({ queryKey: key, exact: true });
    }
  });
};

export const reconcileCaseAcrossListCaches = (caseInfo: Case): void => {
  reconcileQueriesByPrefix<{ pages: PaginatedBody<Case>[]; pageParams: unknown[] }>(
    caseQueryKeys.getInfiniteList(),
    caseInfo,
    reconcileCaseInInfiniteListCache,
    (data) => data.pages.flatMap((page) => page.items),
  );
};

const fetchCasesByIds = async (caseIds: string[]): Promise<Case[]> => {
  if (!caseIds.length) return [];

  const { items } = await api.case.getCaseList({
    itemsPerPage: caseIds.length,
    pageIndex: 0,
    caseIds: caseIds.join(','),
  });
  return items;
};

export const reconcileCasesByIds = async (caseIds: string[]): Promise<void> => {
  if (!caseIds.length) return;

  const cases = await fetchCasesByIds(caseIds);
  cases.forEach(reconcileCaseAcrossListCaches);

  const foundIds = new Set(cases.map((c) => c.caseId));
  caseIds.filter((id) => !foundIds.has(id)).forEach(removeCaseFromAllListCaches);
};

export const updateCaseCache = async (data: Case) => {
  queryClient.setQueryData(caseQueries.item(data.caseId).queryKey, data);
  reconcileCaseAcrossListCaches(data);
  if (data.resources.length) {
    await queryClient.invalidateQueries({ queryKey: caseQueries.caseResourcesData(data.caseId).queryKey });
  }
};
